import { useMemo, useState } from 'react'

import { Badge, DefaultTable, DefaultTooltip, SimpleAlert } from 'twyne-new.ui'

import InputSearchList from '../InputSearchList'

import PasteIntoSelect from './PasteIntoSelect'

export const MODIFIERS = [
  { key: 'encodable', label: 'encodable', suffix: '_urlencoded' },
  { key: 'dba', label: 'dba', suffix: '_dba' },
  { key: 'bid', label: 'bid', suffix: '_bid' },
  { key: 'bidid', label: 'tcpaconsent', suffix: '_tcpaconsent' },
  { key: 'escapable', label: 'escapable', suffix: '_jsescape' },
  { key: 'md5', label: 'md5', suffix: '_md5' },
  { key: 'optout', label: 'optout', suffix: '_optout' },
]

const NOTES = {
  is_email_valid: '(true|false)',
  is_phone_valid: '(true|false)',
  is_postal_valid: '(true|false)',
  is_fraud: '(true|false)',
  is_bot: '(true|false)',
  dob_yyyyddmm: 'Format: yyyyddmm',
  dob_slashes_mmddyyyy: 'Format: mm/dd/yyyy',
  dob_dashes_mmddyyyy: 'Format: mm-dd-yyyy',
}

const REQUIRED = ['pe_key']

export default function PageVariables({ fields = [], paste }) {
  const { selected, insert, insertLabel } = paste
  const [search, setSearch] = useState('')

  const rows = useMemo(() => fields.map(([field, opts]) => ({
    field,
    modifiers: MODIFIERS.filter(({ key }) => opts[key]),
    note: NOTES[field] || '',
  })), [fields])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) {
      return rows
    }

    return rows.filter(({ field, modifiers }) => field.toLowerCase().includes(term) || modifiers.some(({ label }) => label.includes(term)))
  }, [rows, search])

  const columns = useMemo(() => [
    {
      dataField: 'field',
      text: 'Variable',
      headerClassName: 'w-[50%]',
      formatter: (field, { note }) => <div className='flex items-center gap-2'>
        <DefaultTooltip content={insertLabel(`{${field}}`)} triggerProps={{ asChild: true }}>
          <button
            type='button'
            aria-disabled={!selected}
            onClick={() => insert(`{${field}}`)}
            className='font-mono text-sm twy-text-base-accent hover:twy-text-accent cursor-pointer aria-disabled:cursor-not-allowed'
          >
            {`{${field}}`}
          </button>
        </DefaultTooltip>
        {note ? <em className='twy-text-base-neutral text-sm'>- {note}</em> : null}
        {REQUIRED.includes(field) && <Badge variant='light' color='warning' size='sm'>required</Badge>}
      </div>,
    },
    {
      dataField: 'modifiers',
      text: 'Modifiers',
      headerClassName: 'w-[50%]',
      formatter: (modifiers, { field }) => modifiers.length ? <div className='flex flex-wrap gap-1.5'>
        {modifiers.map(({ label, suffix }) => <DefaultTooltip
          key={label}
          content={insertLabel(`{${field}${suffix}}`)}
          triggerProps={{ asChild: true }}
        >
          <Badge variant='light' color='neutral' size='sm' asChild>
            <button
              type='button'
              aria-disabled={!selected}
              onClick={() => insert(`{${field}${suffix}}`)}
              className='cursor-pointer transition-colors hover:twy-subtle-accent hover:twy-text-accent aria-disabled:cursor-not-allowed'
            >
              {label}
            </button>
          </Badge>
        </DefaultTooltip>)}
      </div> : <span className='twy-text-base-neutral'>—</span>,
    },
  ], [insert, insertLabel, selected])

  return <div className='flex flex-col gap-6'>
    <p className='twy-text-base'>
      Variables are replaced with user-specific data when the page renders. Append &ldquo;_urlencoded&rdquo;,
      &ldquo;_jsescape&rdquo; or &ldquo;_md5&rdquo; to any variable that lists (encodable), (escapable) or (md5).
    </p>
    <div className='flex items-end justify-between gap-4'>
      <InputSearchList
        className='w-[50%] h-10 flex items-center bg-transparent! border! twy-border-base rounded-xs [&_input]:py-0'
        placeholder="Filter variables or modifiers"
        value={search}
        onChange={setSearch}
      />
      <PasteIntoSelect className='w-[50%]' selected={selected} />
    </div>
    <div className='h-[48vh] overflow-y-auto border twy-border-base rounded-xs'>
      <DefaultTable
        columns={columns}
        data={filtered}
        keyField='field'
        noDataIndication={() => 'No variables match your filter.'}
      />
    </div>
    <SimpleAlert variant='info' className='my-0'>
      A note about pid, sid1, and sid2: these values are set when the user lands on the landing page (or voucher page),
      from the values specified in the URL, but are optional. Once set from the incoming URL, they are stored in the
      cookie in the user&apos;s browser and persisted across requests throughout the path. If a pid, sid1, or sid2 is
      found in the URL, request body, or the user&apos;s cookie, they will be made available as template variables
      above. If there is no pid, sid1, or sid2, then this value will be an empty string.
    </SimpleAlert>
  </div>
}
